/** @format */

import { CONTENT_LANGUAGES } from 'src/constants';

const EXTRANET_SELECTABLE_LOCALES = ['de', 'en', 'es', 'it', 'fr'];
const EXTRANET_SELECTABLE_LOCALE_SET = new Set(EXTRANET_SELECTABLE_LOCALES);

const PRIMARY_DISPLAY_CODES = new Set(EXTRANET_SELECTABLE_LOCALES);
const SUPPORTED_EXTRANET_LOCALE_ALIASES = new Set(['at', 'ch', 'cz', 'de-de', 'en-uk', 'uk']);

const contentLanguageByValue = new Map(
  CONTENT_LANGUAGES.map(language => [language.value.toLowerCase(), language])
);

function normalizeLocaleCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace('_', '-');
}

export function isExtranetRemovedLocale(locale) {
  const normalized = normalizeLocaleCode(locale);
  if (!normalized) {
    return false;
  }

  return !contentLanguageByValue.has(normalized) && !SUPPORTED_EXTRANET_LOCALE_ALIASES.has(normalized);
}

export function isExtranetSelectableLocale(locale) {
  const normalized = normalizeLocaleCode(locale);
  return EXTRANET_SELECTABLE_LOCALE_SET.has(normalized);
}

export function resolveExtranetSelectableLocale(locale, fallback = 'en') {
  const normalized = normalizeLocaleCode(locale);
  if (isExtranetSelectableLocale(normalized)) {
    return normalized;
  }

  const mapped = resolveExtranetApiLanguageCode(locale);
  const mappedNormalized = normalizeLocaleCode(mapped);
  if (isExtranetSelectableLocale(mappedNormalized)) {
    return mappedNormalized;
  }

  return fallback;
}

function getExtranetSelectableLanguages() {
  return CONTENT_LANGUAGES.filter(
    language => isExtranetSelectableLocale(language.value) && !isExtranetRemovedLocale(language.value)
  );
}

function sortExtranetSelectableLanguages(items) {
  const order = new Map(EXTRANET_SELECTABLE_LOCALES.map((code, index) => [code, index]));

  return [...items].sort((a, b) => {
    const orderA = order.get(normalizeLocaleCode(a.value)) ?? 999;
    const orderB = order.get(normalizeLocaleCode(b.value)) ?? 999;
    return orderA - orderB;
  });
}

export function resolveExtranetApiLanguageCode(locale) {
  const normalized = normalizeLocaleCode(locale);
  if (!normalized || isExtranetRemovedLocale(normalized)) {
    return null;
  }

  if (normalized === 'de-at' || normalized === 'at') {
    return 'de';
  }
  if (normalized === 'de-ch' || normalized === 'ch') {
    return 'de';
  }
  if (normalized === 'cs' || normalized === 'cz') {
    return 'en';
  }
  if (normalized === 'pl') {
    return 'en';
  }
  if (normalized === 'en-gb' || normalized === 'en-uk' || normalized === 'uk') {
    return 'en';
  }

  const exact = contentLanguageByValue.get(normalized);
  return exact ? exact.value : normalized.split('-')[0];
}

export function getExtranetLanguageDisplayCode(locale) {
  const normalized = normalizeLocaleCode(locale);
  if (!normalized || isExtranetRemovedLocale(normalized)) {
    return '';
  }

  if (normalized === 'de-at') {
    return 'AT';
  }
  if (normalized === 'de-ch') {
    return 'CH';
  }
  if (normalized === 'cs' || normalized === 'cz') {
    return 'CZ';
  }
  if (normalized === 'pl') {
    return 'PL';
  }
  if (normalized === 'en-gb' || normalized === 'uk') {
    return 'UK';
  }

  return normalized.split('-')[0].toUpperCase();
}

function getExtranetLanguageName(locale, t) {
  const normalized = normalizeLocaleCode(locale);
  if (!normalized || isExtranetRemovedLocale(normalized)) {
    return '';
  }

  if (normalized === 'de-at' || normalized === 'de-ch') {
    return t('extranetLanguages.de');
  }
  if (normalized === 'en-gb' || normalized === 'uk') {
    return t('extranetLanguages.en');
  }

  const base = normalized.split('-')[0];
  if (PRIMARY_DISPLAY_CODES.has(base)) {
    return t(`extranetLanguages.${base}`);
  }

  const exact = contentLanguageByValue.get(normalized);
  return exact?.title || base.toUpperCase();
}

function getExtranetLanguageDisplayName(locale, t) {
  return getExtranetLanguageName(locale, t);
}

function getExtranetLanguageDisplayLabel(locale, t) {
  const name = getExtranetLanguageName(locale, t);
  const code = getExtranetLanguageDisplayCode(locale);
  if (!name || !code) {
    return '';
  }

  return `${name} - ${code}`;
}

export function formatExtranetLanguageList(languages, t, separator = ' | ') {
  const rawList = Array.isArray(languages)
    ? languages
    : typeof languages === 'string' && languages.trim()
      ? languages
          .split('|')
          .map(entry => entry.trim())
          .filter(Boolean)
      : [];

  if (rawList.length === 0) {
    return '';
  }

  const seen = new Set();
  const labels = [];

  for (const entry of rawList) {
    if (isExtranetRemovedLocale(entry)) {
      continue;
    }

    const normalized = normalizeLocaleCode(entry);
    if (!normalized || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    const label = getExtranetLanguageDisplayLabel(entry, t);
    if (label) {
      labels.push(label);
    }
  }

  return labels.join(separator);
}

export function formatExtranetLanguageCodeList(languages, separator = ' | ') {
  const rawList = Array.isArray(languages)
    ? languages
    : typeof languages === 'string' && languages.trim()
      ? languages
          .split('|')
          .map(entry => entry.trim())
          .filter(Boolean)
      : [];

  if (rawList.length === 0) {
    return '';
  }

  const seen = new Set();
  const codes = [];

  for (const entry of rawList) {
    if (isExtranetRemovedLocale(entry)) {
      continue;
    }

    const normalized = normalizeLocaleCode(entry);
    if (!normalized || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    const code = getExtranetLanguageDisplayCode(entry);
    if (code) {
      codes.push(code);
    }
  }

  return codes.join(separator);
}

export function getExtranetFilterLanguageItems(t, { includeMarket = false } = {}) {
  return sortExtranetSelectableLanguages(
    getExtranetSelectableLanguages().map(language => {
      const label = getExtranetLanguageDisplayName(language.value, t);
      return {
        key: language.value,
        value: language.value,
        label: includeMarket ? `${language.market} - ${label}` : label,
        market: language.market
      };
    })
  );
}

export function getExtranetLanguageMenuItems(t) {
  return sortExtranetSelectableLanguages(
    getExtranetSelectableLanguages().map(language => ({
      key: language.value,
      value: language.value,
      label: getExtranetLanguageDisplayName(language.value, t),
      market: language.market
    }))
  );
}

export function getExtranetEditorLanguageOptions(t) {
  return sortExtranetSelectableLanguages(
    getExtranetSelectableLanguages().map(language => ({
      value: language.value,
      title: getExtranetLanguageDisplayName(language.value, t),
      market: language.market
    }))
  );
}
